import { Link } from "react-router-dom";
import { ArrowRight, Check, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { VENUES } from "@/lib/site";

const SEASIDE_IMG = "https://images.unsplash.com/photo-1577997146693-a20148a112f3?w=1600&q=80";

export default function Locations() {
  return (
    <>
      <Seo
        title="Our Whitstable Locations"
        description={"Kerry's dance classes run at The Umbrella Centre and the Whitstable Swimming Pool dance studio, with private lessons at Treetops Dance Studio — all in Whitstable. Directions, access notes and maps."}
        path="/locations"
      />
      <section className="grain border-b-[0.5px] border-foreground/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24">
          <FadeUp className="max-w-3xl">
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground mb-6">Whitstable, Kent</p>
            <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
              Dance classes
              <br />
              <span className="text-[hsl(var(--kerrys-sea))]">in Whitstable</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Weekly classes run at three much-loved Whitstable venues, with one-to-one private lessons at a fourth
              &mdash; all easy to reach, all minutes from the sea.
            </p>
          </FadeUp>
        </div>
      </section>

      <div className="border-b-[0.5px] border-foreground/30">
        <img src={SEASIDE_IMG} alt="Whitstable seafront with colourful beach huts" className="w-full h-40 sm:h-56 object-cover" loading="lazy" />
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px] space-y-[120px]">
        {VENUES.map((v, i) => {
          const flip = i % 2 === 1;
          return (
            <FadeUp key={v.id}>
              <article className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-[0.5px] border-foreground bg-card" data-testid="locations-location-card">
                <div className={`lg:col-span-6 p-8 sm:p-12 ${flip ? "lg:order-2" : ""}`}>
                  <p className="font-display text-2xl text-muted-foreground">0{i + 1}</p>
                  <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.9] mt-2">{v.name}</h2>
                  <p className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground mt-4">{v.address}</p>
                  {v.tag && (
                    <p className="inline-flex mt-4 px-3 py-1.5 bg-[hsl(var(--kerrys-sun))] border-[0.5px] border-foreground text-[11px] font-bold uppercase tracking-[0.15em]">{v.tag}</p>
                  )}
                  <p className="text-muted-foreground mt-5 leading-relaxed max-w-prose">{v.blurb}</p>
                  <ul className="mt-6 space-y-2.5">
                    {v.notes.map((n) => (
                      <li key={n} className="flex items-center gap-3 text-sm font-medium">
                        <span className="h-5 w-5 bg-[hsl(var(--kerrys-lime))] border-[0.5px] border-foreground flex items-center justify-center shrink-0">
                          <Check className="h-3 w-3 text-foreground" />
                        </span>
                        {n}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-4 mt-9">
                    <Button
                      asChild
                      className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
                      data-testid="locations-directions-button"
                    >
                      <a href={v.mapsUrl} target="_blank" rel="noopener noreferrer">
                        <Navigation className="h-4 w-4 mr-2" /> Get directions
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
                      data-testid="locations-timetable-button"
                    >
                      <Link to={v.privateOnly ? "/private-lessons" : "/timetable"}>
                        {v.privateOnly ? "Private lessons" : "Classes here"} <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </div>
                <div className={`lg:col-span-6 min-h-[300px] lg:min-h-[520px] ${flip ? "lg:order-1 lg:border-r-[0.5px]" : "lg:border-l-[0.5px]"} border-t-[0.5px] lg:border-t-0 border-foreground`}>
                  <iframe
                    title={`Map showing ${v.name}`}
                    src={v.embedUrl}
                    className="w-full h-full min-h-[300px] lg:min-h-[520px] border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid={`locations-map-${v.id}`}
                  />
                </div>
              </article>
            </FadeUp>
          );
        })}
      </section>
    </>
  );
}
